import React, { useState } from 'react';
import { Box, Fab, Modal, Portal, Typography, IconButton, Button, Badge } from '@mui/material';
import {
  DataGrid,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  useGridApiContext,
  GridRenderCellParams,
} from '@mui/x-data-grid';

import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import { deleteSavedCluster } from '../helper';
import { exchangeRates } from 'src/const';
import { COLOR_PRIMARY_2 } from 'src/colors';

const styles = {
  fab: {
    position: 'absolute',
    bottom: 24,
    left: 24,
    zIndex: 99999,
    color: '#fff',
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    borderRadius: '20px',
    padding: '20px',
    width: 'clamp(300px, 90%, 800px)',
    height: '70vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  clusterList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
};

interface FloatingClusterButtonProps {
  marketID: string;
  currency: string;
  savedClusters: string[];
  setSavedClusters: React.Dispatch<React.SetStateAction<any[]>>;
  isUserAllowed: boolean;
}

const CustomToolbar = ({
  marketID,
  savedClusters,
  setSavedClusters,
}: {
  marketID: string;
  savedClusters: string[];
  setSavedClusters: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
  const apiRef = useGridApiContext();

  const handleClearAll = () => {
    deleteSavedCluster(marketID, null);
    setSavedClusters([]);
  };

  return (
    <Box
      sx={{
        position: 'sticky !important',
        left: 0,
        minWidth: '300px',
        maxWidth: '400px',
      }}
    >
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ utf8WithBom: true }} />
        <Button
          size="small"
          onClick={handleClearAll}
          startIcon={<DeleteOutlineIcon />}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          Clear All
        </Button>
      </GridToolbarContainer>
    </Box>
  );
};

const FloatingClusterButton: React.FC<FloatingClusterButtonProps> = ({
  marketID,
  currency,
  savedClusters,
  setSavedClusters,
  isUserAllowed,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  if (!savedClusters || Object.keys(savedClusters).length === 0) return null;

  const handleClick = () => {
    setIsModalOpen(true);
    console.log(savedClusters);
  };

  const getSavedClusterCount = () => {
    const count = Object.keys(savedClusters).length;
    if (count > 99) return '99+';
    if (count > 9) return '9+';
    return String(count);
  };

  const savedClusterCount = getSavedClusterCount();
  const currencySymbol = exchangeRates[currency].symbol;

  // Render locked cell
  const renderLockedCell = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LockIcon fontSize="small" color="disabled" />
    </Box>
  );

  return (
    <>
      <Portal container={document.getElementById('root')}>
        <Fab color="primary" variant="extended" sx={styles.fab} onClick={handleClick}>
          Saved Areas{' '}
          {savedClusterCount !== '0' && (
            <span
              style={{
                marginLeft: '4px',
                borderRadius: '200px',
                backgroundColor: '#fff',
                color: COLOR_PRIMARY_2,
                width: '24px',
                height: '24px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {savedClusterCount}
            </span>
          )}
        </Fab>
      </Portal>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={styles.modal}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              mb: 2,
            }}
          >
            <Typography variant="h6">Saved Areas</Typography>
            <IconButton
              onClick={() => setIsModalOpen(false)}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
              autoHeight
              rows={Object.values(savedClusters).map((cluster: any, idx: number) => ({
                id: idx,
                name: cluster.name,
                listingCount: cluster.listingCount,
                adr: cluster.adr,
                occupancyRate: cluster.occupancyRate,
                revPar: cluster.revPar,
              }))}
              columns={[
                { field: 'name', headerName: 'Name', flex: 1 },
                { field: 'listingCount', headerName: 'Listing Count', type: 'number', flex: 1 },
                {
                  field: 'adr',
                  headerName: `ADR (${currencySymbol})`,
                  type: 'number',
                  flex: 1,
                  renderCell: (params: GridRenderCellParams) =>
                    isUserAllowed ? params.value : renderLockedCell(),
                },
                {
                  field: 'occupancyRate',
                  headerName: 'Occupancy Rate (%)',
                  type: 'number',
                  flex: 1,
                  renderCell: (params: GridRenderCellParams) =>
                    isUserAllowed ? params.value : renderLockedCell(),
                },
                {
                  field: 'revPar',
                  headerName: `RevPAR (${currencySymbol})`,
                  type: 'number',
                  flex: 1,
                  renderCell: (params: GridRenderCellParams) =>
                    isUserAllowed ? params.value : renderLockedCell(),
                },
              ]}
              components={{
                Toolbar: props => (
                  <CustomToolbar
                    {...props}
                    marketID={marketID}
                    setSavedClusters={setSavedClusters}
                  />
                ),
              }}
              sx={{
                width: '100%',
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FloatingClusterButton;
